import ExpandMore from "@mui/icons-material/ExpandMore";
import { Button, Menu, MenuList, useTheme } from "@mui/material";
import { useGridSelection } from "hooks/omnisearch-v2";
import { createContext, Dispatch, SetStateAction, useContext, useState } from "react";

import { SetDowntimeWindowDialog, SetDowntimeWindowMenuItem } from "./SetDowntimeWindowDialog";

const buttonId = "portals-bulk-actions-button";
const menuId = "portals-bulk-actions-menu";

type DialogState = "setDowntimeWindow" | null;

const bulkActionContext = createContext<{
  dialogState: DialogState;
  setDialogState: Dispatch<SetStateAction<DialogState>>;
  onClose: () => void;
}>({
  dialogState: null,
  setDialogState: () => {},
  onClose: () => {},
});

export function useBulkActionContext() {
  return useContext(bulkActionContext);
}

export function BulkActionsMenu() {
  const { selectedCount } = useGridSelection();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [dialogState, setDialogState] = useState<DialogState>(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();

  const onClose = () => {
    setAnchorEl(null);
    setDialogState(null);
  };

  return (
    <bulkActionContext.Provider value={{ onClose, dialogState, setDialogState }}>
      <Button
        id={buttonId}
        size="medium"
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        disabled={selectedCount === 0}
        variant="contained"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={<ExpandMore />}
        sx={{ whiteSpace: "nowrap", backgroundColor: theme.palette.secondary.main }}
      >
        Bulk Actions ({selectedCount})
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        MenuListProps={{ "aria-labelledby": buttonId }}
      >
        <MenuList>
          <SetDowntimeWindowMenuItem />
        </MenuList>
      </Menu>
      {/* Mounted only while open so a cancelled entry can't reappear on reopen. */}
      {dialogState === "setDowntimeWindow" && <SetDowntimeWindowDialog />}
    </bulkActionContext.Provider>
  );
}
